/** Телеметрия GenerationJob (It2tv::GenerationTelemetry.snapshot): фаза, пульс, опросы провайдера. */
export type GenerationJobTelemetry = {
  phase: string | null
  phase_started_at: string | null
  heartbeat_at: string | null
  provider_task_id: string | null
  provider_status: string | null
  poll_count: number
  last_poll_at: string | null
  next_poll_at: string | null
  phase_meta: Record<string, unknown> | null
}

export type GenerationPhaseKey =
  | "units.sec"
  | "units.min"
  | "units.hour"
  | "units.hourMin"
  | "polls"
  | "queued"
  | "claimed"
  | "resumeWait"
  | "preparing_refs"
  | "submitting"
  | "submittingTimeout"
  | "in_flight"
  | "provider"
  | "providerNoPolls"
  | "parked"
  | "parkedDue"
  | "downloading"
  | "attaching"
  | "stalled"
  | "stalledPreparingRefs"
  | "stalledParkOverdue"
  | "unknownStatus"

export type GenerationPhaseTranslate = (key: GenerationPhaseKey, options?: Record<string, string | number>) => string

const PROVIDER_LABELS: [RegExp, string][] = [
  [/^byteplus/, "BytePlus"],
  [/^kling/, "Kling"],
  [/^(dashscope|wan|qwen)/, "DashScope"],
  [/^(google|gemini|veo)/, "Google"],
  [/^open_?router/, "OpenRouter"],
  [/^topaz/, "Topaz"],
  [/^sync/, "Sync"],
  [/^elevenlabs/, "ElevenLabs"],
  [/^minimax/, "MiniMax"],
  [/^flux/, "Flux"],
  [/^video_depth/, "Video Depth"],
]

export function generationProviderLabel(provider: unknown): string {
  const raw = typeof provider === "string" ? provider : ""
  if (!raw) return "API"
  const hit = PROVIDER_LABELS.find(([pattern]) => pattern.test(raw.toLowerCase()))
  return hit ? hit[1] : raw
}

/** «1:52», «1:02:07» — для отсчётов, где важны секунды (запрос в полёте, таймаут). */
export function formatPhaseClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
  return `${m}:${String(s).padStart(2, "0")}`
}

/** «40 с», «3 мин», «2 ч 5 мин» — для длительностей фаз. */
export function formatPhaseSpan(ms: number, t: GenerationPhaseTranslate): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  if (total < 60) return t("units.sec", { count: total })
  const minutes = Math.floor(total / 60)
  if (minutes < 60) return t("units.min", { count: minutes })
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest > 0 ? t("units.hourMin", { hours, minutes: rest }) : t("units.hour", { count: hours })
}

function msSince(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null
  const at = Date.parse(iso)
  return Number.isNaN(at) ? null : Math.max(0, now - at)
}

function msUntil(iso: unknown, now: number): number | null {
  if (typeof iso !== "string" || !iso) return null
  const at = Date.parse(iso)
  return Number.isNaN(at) ? null : at - now
}

/**
 * Фаза генерации человеческим языком: «В очереди 3 мин», «Отправлено в BytePlus 1:52 назад, таймаут через 0:08»,
 * «У провайдера queued, 14 опросов, последний 8 с назад», «Нет пульса 5 мин». null — фазы нет или она терминальна.
 */
export function describeGenerationPhase(
  telemetry: GenerationJobTelemetry | null | undefined,
  now: number,
  t: GenerationPhaseTranslate,
): string | null {
  if (!telemetry?.phase) return null
  const meta = telemetry.phase_meta ?? {}
  const inPhase = msSince(telemetry.phase_started_at, now) ?? 0
  const span = (ms: number | null) => formatPhaseSpan(ms ?? 0, t)
  const provider = generationProviderLabel(meta.provider)
  const status = telemetry.provider_status || t("unknownStatus")
  const polls = t("polls", { count: telemetry.poll_count })

  // Слайс опроса отдан: задача по-прежнему у провайдера, ждём воркер для следующей проверки.
  if (meta.resume === true && (telemetry.phase === "queued" || telemetry.phase === "claimed")) {
    return t("resumeWait", { status, polls, elapsed: span(inPhase) })
  }

  switch (telemetry.phase) {
    case "queued":
      return t("queued", { elapsed: span(inPhase) })
    case "claimed":
      return t("claimed", { ago: span(inPhase) })
    case "preparing_refs":
      return t("preparing_refs", { elapsed: span(inPhase) })
    case "submitting": {
      const left = msUntil(meta.deadline_at, now)
      if (left == null) return t("submitting", { provider, ago: formatPhaseClock(inPhase) })
      return t("submittingTimeout", { provider, ago: formatPhaseClock(inPhase), left: formatPhaseClock(left) })
    }
    case "in_flight":
      return t("in_flight", { provider, ago: formatPhaseClock(inPhase) })
    case "provider_queued":
    case "provider_running": {
      const lastPoll = msSince(telemetry.last_poll_at, now)
      if (telemetry.poll_count > 0 && lastPoll != null) return t("provider", { status, polls, ago: span(lastPoll) })
      return t("providerNoPolls", { status, elapsed: span(inPhase) })
    }
    case "parked": {
      const left = msUntil(telemetry.next_poll_at, now)
      if (left != null && left < 0) return t("parkedDue", { status, polls, ago: span(-left) })
      return t("parked", { status, polls, left: span(left) })
    }
    case "downloading":
      return t("downloading", { elapsed: span(inPhase) })
    case "attaching":
      return t("attaching", { elapsed: span(inPhase) })
    case "stalled": {
      if (meta.reason === "preparing_refs") {
        const started = typeof meta.stalled_from_started_at === "string" ? meta.stalled_from_started_at : null
        return t("stalledPreparingRefs", { elapsed: span(msSince(started, now) ?? inPhase) })
      }
      if (meta.reason === "park_overdue") {
        return t("stalledParkOverdue", { ago: span(-(msUntil(telemetry.next_poll_at, now) ?? 0)) })
      }
      return t("stalled", { ago: span(msSince(telemetry.heartbeat_at, now)) })
    }
    default:
      return null
  }
}
